/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductList, ProductListsDataActions } from '@msdyn365-commerce/retail-proxy';

/**
 * Input class for the CreateProductList data action.
 */
export class CreateProductListInput implements IActionInput {
    public readonly templateName: string;

    public readonly templateType: number;

    public constructor(templateName: string, templateType: number) {
        this.templateName = templateName;
        this.templateType = templateType;
    }

    /**
     * GetCacheObjectType.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => 'CreateProductListInput';

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheObjectType = (): string => 'CreateProductListInput';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the CreateProductList method.
 * @param inputData - The input data passed to the createInput method.
 * @param templateName - The name of template.
 * @param templateType - The type of template.
 * @returns - The Action input.
 */
export const createProductListInput = (inputData: ICreateActionContext, templateName: string, templateType: number): CreateProductListInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create product list. User is not authenticated.');
    }
    return new CreateProductListInput(templateName, templateType);
};

/**
 * The action method for the CreateProductList data action.
 * @param input - The action input.
 * @param context - The action context.
 * @returns - ProductList object.
 */
export async function createProductListAction(input: CreateProductListInput, context: IActionContext): Promise<ProductList> {
    try {
        const orderTemplate: ProductList = {
            Id: '',
            Name: input.templateName,
            ProductListTypeValue: input.templateType
        };
        const createdProductList = await ProductListsDataActions.createProductListAsync({
            callerContext: context
        }, orderTemplate);
        if (createdProductList.Id !== '') {
            return createdProductList;
        }
        context.telemetry.error('Created product list was null');
        throw new Error('Created product list was null');

    } catch (error) {
        context.telemetry.error('Not able to create product list', error);
        throw error;
    }
}

/**
 * The CreateProductList Data Action.
 * Returns created Product List.
 */
export const createProductListActionDataAction = createObservableDataAction<ProductList>({
    id: '@msdyn365-commerce-modules/retail-actions/create-product-list',
    action: createProductListAction as IAction<ProductList>,
    input: createProductListInput
});

export default createProductListActionDataAction;
